import type { SVGProps } from 'react';

import { cn } from '@/lib/utils';

/**
 * Hand-drawn line sketches for empty states. Inline SVG, no image files: the lines take the text
 * colour and the accent takes `--primary`, so every sketch follows light/dark and the colour
 * preset the person picked. One stroke weight and one accent per sketch keeps them a family —
 * add a new one in the same style rather than importing artwork that will not match.
 *
 * Each sketch has ONE moving part, wrapped in a `<g className="art-…">` (swing, pop, hop, sweep;
 * keyframes in the global CSS) that moves once, shortly after the sketch appears, then rests —
 * never looped. More than one mover per sketch starts to look busy.
 */
type ArtProps = SVGProps<SVGSVGElement>;

function Sketch({ className, children, ...props }: ArtProps) {
  return (
    <svg
      viewBox="0 0 120 96"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={cn('text-foreground/70', className)}
      {...props}
    >
      {children}
    </svg>
  );
}

const ACCENT = 'fill-primary/15 stroke-primary';

/** A clipboard checklist — tasks. */
export function TasksArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M35 17 L85 18 Q87 18 87 20 L86 82 Q86 84 84 84 L36 85 Q34 85 34 83 L33 19 Q33 17 35 17 Z" className="fill-card" />
      <rect x="49" y="11" width="22" height="11" rx="3" className={ACCENT} />
      <g className="art-pop">
        <rect x="42" y="33" width="9" height="9" rx="2" className={ACCENT} />
      <path d="M44 37.5 l2.5 2.5 l5 -6" className="stroke-primary" />
      </g>
      <path d="M57 38 h21" />
      <rect x="42" y="50" width="9" height="9" rx="2" />
      <path d="M57 55 h17" />
      <rect x="42" y="67" width="9" height="9" rx="2" />
      <path d="M57 72 h19" />
    </Sketch>
  );
}

/** Contact cards with a plus — leads, customers, projects. */
export function AccountsArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M28 30 L78 25 L82 64 L32 69 Z" strokeDasharray="1 5" />
      <path d="M38 35 h50 q3 0 3 3 v35 q0 3 -3 3 h-50 q-3 0 -3 -3 v-35 q0 -3 3 -3 Z" className="fill-card" />
      <circle cx="52" cy="51" r="7" className={ACCENT} />
      <path d="M42 70 q10 -11 20 0" />
      <path d="M68 47 h15 M68 55 h11 M68 63 h13" />
      <g className="art-pop">
        <circle cx="93" cy="31" r="9" className={ACCENT} />
      <path d="M93 26.5 v9 M88.5 31 h9" className="stroke-primary" />
      </g>
    </Sketch>
  );
}

/** A calendar page with one day picked out. */
export function CalendarArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M31 25 h58 q3 0 3 3 v51 q0 3 -3 3 h-58 q-3 0 -3 -3 v-51 q0 -3 3 -3 Z" className="fill-card" />
      <path d="M28 39 h64" />
      <path d="M45 19 v12 M75 19 v12" />
      <g strokeWidth={3}>
        <path d="M41 51 h0.1 M53 51 h0.1 M65 51 h0.1 M77 51 h0.1 M41 63 h0.1 M53 63 h0.1 M77 63 h0.1 M41 74 h0.1 M53 74 h0.1 M65 74 h0.1" />
      </g>
      <g className="art-hop">
        <rect x="59" y="57" width="12" height="12" rx="2.5" className={ACCENT} />
      </g>
    </Sketch>
  );
}

/** A bell with a tick — nothing waiting. */
export function BellArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <g className="art-swing">
        <path d="M60 13 v6" />
      <path d="M60 19 q-18 0 -18 22 v14 l-7 9 h50 l-7 -9 v-14 q0 -22 -18 -22 Z" className="fill-card" />
      <path d="M53 68 q7 9 14 0" />
      </g>
      <path d="M31 29 q-5 7 0 14 M24 25 q-8 11 0 22" strokeWidth={1.8} />
      <g className="art-pop" style={{ animationDelay: '0.5s' }}>
        <circle cx="86" cy="27" r="10" className={ACCENT} />
      <path d="M81 27 l3.5 3.5 l6.5 -7.5" className="stroke-primary" />
      </g>
    </Sketch>
  );
}

/** A magnifier over a trail that goes nowhere — not found. */
export function LostArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M10 78 q18 -16 36 -7 t38 -6 t28 -12" strokeDasharray="3 6" />
      <g className="art-sweep">
        <circle cx="58" cy="38" r="17" className={ACCENT} />
      <path d="M71 51 l15 15" strokeWidth={4.5} />
      <path d="M53 34 q0 -6 5 -6 q5 0 5 5 q0 4 -5 5 v3" className="stroke-primary" />
      <path d="M58 46.5 h0.1" strokeWidth={3} className="stroke-primary" />
      </g>
    </Sketch>
  );
}

/** Two speech bubbles — a discussion not started. */
export function CommentsArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M24 22 h46 q4 0 4 4 v24 q0 4 -4 4 h-30 l-10 9 v-9 h-6 q-4 0 -4 -4 v-24 q0 -4 4 -4 Z" className="fill-card" />
      <path d="M32 33 h30 M32 42 h20" />
      <g className="art-hop">
        <path d="M58 46 h34 q4 0 4 4 v20 q0 4 -4 4 h-4 v8 l-9 -8 h-21 q-4 0 -4 -4 v-20 q0 -4 4 -4 Z" className={ACCENT} />
      <path d="M66 57 h20 M66 65 h12" className="stroke-primary" />
      </g>
    </Sketch>
  );
}

/** A page with a paperclip — no files. */
export function FilesArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M40 16 h30 l14 14 v48 q0 3 -3 3 h-41 q-3 0 -3 -3 v-59 q0 -3 3 -3 Z" className="fill-card" />
      <path d="M70 16 v12 q0 2 2 2 h12" />
      <path d="M46 46 h28 M46 55 h28 M46 64 h18" />
      <g className="art-swing">
        <path d="M33 30 v-8 q0 -7 6 -7 q6 0 6 7 v20 q0 4 -4 4 q-4 0 -4 -4 v-16" className="stroke-primary" strokeWidth={2.6} />
      </g>
    </Sketch>
  );
}

/** A door with a padlock — not let in (yet). */
export function LockedArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M40 84 v-62 q0 -4 4 -4 h32 q4 0 4 4 v62" className="fill-card" />
      <path d="M30 84 h60" />
      <path d="M71 52 h0.1" strokeWidth={3.5} />
      <g className="art-swing">
        <path d="M54 46 v-6 q0 -7 6 -7 q6 0 6 7 v6" className="stroke-primary" />
        <rect x="50" y="46" width="20" height="16" rx="3" className={ACCENT} />
        <path d="M60 52 v4" className="stroke-primary" />
      </g>
    </Sketch>
  );
}

/** A waving hand — hello. */
export function WaveArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <g className="art-swing" style={{ transformOrigin: '50% 100%' }}>
        <path
          d="M50 82 q-14 -4 -16 -20 l-4 -14 q-1 -5 3 -6 q4 -1 6 4 l4 10 v-30 q0 -5 4 -5 q4 0 4 5 v22 v-28 q0 -5 4 -5 q4 0 4 5 v28 v-24 q0 -5 4 -5 q4 0 4 5 v26 v-18 q0 -5 4 -5 q4 0 4 5 v26 q0 20 -18 24 Z"
          className="fill-card"
        />
      </g>
      <path d="M86 26 q6 4 6 12 M93 20 q9 7 9 18" className="stroke-primary" />
      <path d="M22 30 l4 3 M18 42 h5 M24 20 l3 4" className="stroke-primary" />
    </Sketch>
  );
}

/** A notepad laid out as a week, a pencil about to write. */
export function PlanArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M26 22 h58 q3 0 3 3 v54 q0 3 -3 3 h-58 q-3 0 -3 -3 v-54 q0 -3 3 -3 Z" className="fill-card" />
      <path d="M33 16 v10 M45 16 v10 M57 16 v10 M69 16 v10" />
      <path d="M23 36 h64 M45 36 v46 M66 36 v46" strokeWidth={1.6} />
      <path d="M30 46 h9 M30 54 h7 M52 46 h8" />
      <g className="art-hop">
        <path d="M92 30 l8 8 l-30 30 l-10 2 l2 -10 Z" className={ACCENT} />
        <path d="M86 36 l8 8" className="stroke-primary" />
      </g>
    </Sketch>
  );
}

/** An org chart — departments under a vertical. */
export function OrgArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <rect x="47" y="12" width="26" height="16" rx="3" className="fill-card" />
      <path d="M60 28 v12 M30 40 h60 M30 40 v10 M60 40 v10 M90 40 v10" />
      <rect x="18" y="50" width="24" height="15" rx="3" className="fill-card" />
      <rect x="78" y="50" width="24" height="15" rx="3" className="fill-card" />
      <g className="art-pop">
        <rect x="48" y="50" width="24" height="15" rx="3" className={ACCENT} />
        <path d="M60 54 v7 M56.5 57.5 h7" className="stroke-primary" />
      </g>
    </Sketch>
  );
}

/** Three people, the middle one picked out — a team. */
export function TeamArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <circle cx="34" cy="40" r="8" className="fill-card" />
      <path d="M22 70 q12 -18 24 0" />
      <circle cx="86" cy="40" r="8" className="fill-card" />
      <path d="M74 70 q12 -18 24 0" />
      <g className="art-hop">
        <circle cx="60" cy="34" r="10" className={ACCENT} />
        <path d="M47 72 q13 -24 26 0" className="stroke-primary" />
      </g>
      <path d="M16 80 h88" strokeWidth={1.6} strokeDasharray="2 5" />
    </Sketch>
  );
}

/** A laptop with a tag — company hardware. */
export function DeviceArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M30 24 h60 q3 0 3 3 v38 h-66 v-38 q0 -3 3 -3 Z" className="fill-card" />
      <path d="M18 65 h84 l-5 9 q-1 2 -3 2 h-68 q-2 0 -3 -2 Z" className="fill-card" />
      <path d="M52 70 h16" />
      <g className="art-swing">
        <path d="M88 36 l12 0 l6 8 l-6 8 h-12 Z" className={ACCENT} />
        <path d="M93 44 h0.1" strokeWidth={3} className="stroke-primary" />
        <path d="M80 30 q4 6 8 14" strokeWidth={1.6} />
      </g>
    </Sketch>
  );
}

/** A spanner turning — out for repair. */
export function WrenchArt(props: ArtProps) {
  return (
    <Sketch {...props}>
      <path d="M26 72 h68" strokeDasharray="2 6" strokeWidth={1.6} />
      <rect x="34" y="40" width="36" height="24" rx="3" className="fill-card" />
      <path d="M40 48 h18 M40 56 h12" />
      <g className="art-swing" style={{ transformOrigin: '20% 80%' }}>
        <path
          d="M74 54 l16 -16 q-3 -9 4 -14 q5 -3 10 -1 l-7 7 l2 6 l6 2 l7 -7 q2 5 -1 10 q-5 7 -14 4 l-16 16 q-3 3 -6 0 q-3 -3 -1 -7 Z"
          className={ACCENT}
        />
      </g>
    </Sketch>
  );
}
